"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.CALENDAR_SOURCE_LABELS = exports.CALENDAR_SOURCES = exports.assignUserCalendarSchema = exports.holidayYearQuerySchema = exports.addHolidaySchema = exports.updateCalendarSchema = exports.createCalendarSchema = exports.isoDateSchema = exports.timeOfDaySchema = exports.timezoneSchema = exports.SUPPORTED_TIMEZONES = exports.DEFAULT_CALENDAR = exports.WORKING_DAYS_MAX = exports.WORKING_DAYS_MON_TO_FRI = exports.WORKING_DAYS_SUN_TO_THU = exports.DAYS_OF_WEEK = void 0;
exports.hasWorkingDay = hasWorkingDay;
exports.toggleWorkingDay = toggleWorkingDay;
exports.describeWorkingDays = describeWorkingDays;
exports.isValidTimezone = isValidTimezone;
exports.isRealIsoDate = isRealIsoDate;
const zod_1 = require("zod");
const schemas_1 = require("./schemas");
// D14 — the working week and the holiday calendar. Every SLA clock in the system
// excludes non-working time (L7), so this is the one piece of configuration that
// decides whether a number the product reports is defensible.
//
// **Times are wall-clock in the calendar's own timezone.** D14 says the business
// runs on BD time, which is why `Asia/Dhaka` is the default — but the model gives
// every calendar a timezone of its own, and the mandatory DST test cases only mean
// anything if 09:00 on a Europe/London calendar is 09:00 in London. A calendar's
// hours are therefore read in its own zone, never converted first.
/**
 * Working days are a bitmask, **bit 0 = Sunday** through bit 6 = Saturday.
 *
 * Sunday-first because the default working week here is Sunday-Thursday, and
 * because `Date.getDay()` numbers the days the same way — so the two never need a
 * translation table between them. Luxon numbers Monday 1 to Sunday 7, which is the
 * one place a conversion is needed, and it happens in exactly one function.
 */
exports.DAYS_OF_WEEK = [
    { bit: 0, short: 'Sun', label: 'Sunday' },
    { bit: 1, short: 'Mon', label: 'Monday' },
    { bit: 2, short: 'Tue', label: 'Tuesday' },
    { bit: 3, short: 'Wed', label: 'Wednesday' },
    { bit: 4, short: 'Thu', label: 'Thursday' },
    { bit: 5, short: 'Fri', label: 'Friday' },
    { bit: 6, short: 'Sat', label: 'Saturday' },
];
exports.WORKING_DAYS_SUN_TO_THU = 0b0011111;
exports.WORKING_DAYS_MON_TO_FRI = 0b0111110;
exports.WORKING_DAYS_MAX = 0b1111111;
/** The default working week, per D14 and the S6 acceptance criteria. */
exports.DEFAULT_CALENDAR = {
    timezone: 'Asia/Dhaka',
    workingDays: exports.WORKING_DAYS_SUN_TO_THU,
    startTime: '09:45',
    endTime: '18:45',
};
function hasWorkingDay(workingDays, bit) {
    return (workingDays & (1 << bit)) !== 0;
}
function toggleWorkingDay(workingDays, bit) {
    return workingDays ^ (1 << bit);
}
/** "Sun-Thu", "Mon, Wed, Fri", "No working days" — for a list row, not a form. */
function describeWorkingDays(workingDays) {
    const active = exports.DAYS_OF_WEEK.filter((day) => hasWorkingDay(workingDays, day.bit));
    if (active.length === 0)
        return 'No working days';
    if (active.length === exports.DAYS_OF_WEEK.length)
        return 'Every day';
    // Only a genuinely contiguous run collapses to a dash. Sat+Sun is contiguous on a
    // Sunday-first week only if it wraps, and a wrapped range reads worse than a list.
    const isRun = active.every((day, i) => i === 0 || day.bit === (active[i - 1]?.bit ?? -9) + 1);
    if (isRun && active.length > 2) {
        return `${active[0]?.short}-${active[active.length - 1]?.short}`;
    }
    return active.map((day) => day.short).join(', ');
}
/**
 * The zones offered in the picker. Deliberately short: D13 settles on Asia/Dhaka
 * system-wide with a per-user override for UK staff, and a 400-entry dropdown is a
 * worse answer to a two-country company than a three-entry one. The server accepts
 * any valid IANA zone, so adding a fourth is one line here and no migration.
 */
exports.SUPPORTED_TIMEZONES = [
    { value: 'Asia/Dhaka', label: 'Asia/Dhaka — Bangladesh' },
    { value: 'Europe/London', label: 'Europe/London — United Kingdom' },
    { value: 'UTC', label: 'UTC — coordinated universal time' },
];
/** Rejects anything `Intl` cannot resolve, which is the only honest test of a zone name. */
exports.timezoneSchema = zod_1.z
    .string()
    .trim()
    .min(1, 'Choose a timezone.')
    .refine(isValidTimezone, 'That is not a recognised timezone.');
function isValidTimezone(zone) {
    try {
        new Intl.DateTimeFormat('en-GB', { timeZone: zone });
        return true;
    }
    catch {
        return false;
    }
}
/**
 * `HH:mm`, and `HH:mm:ss` on the way back out of Postgres, which returns `time`
 * columns with seconds. Normalised to `HH:mm` so a form and a database row never
 * disagree about what the same time looks like.
 */
exports.timeOfDaySchema = zod_1.z
    .string()
    .trim()
    .regex(/^([01]\d|2[0-3]):[0-5]\d(:[0-5]\d)?$/, 'Enter a time in the form 09:45.')
    .transform((value) => value.slice(0, 5));
const ISO_DATE_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/;
/** Whether `yyyy-mm-dd` names a day that exists: 2026-02-30 has the right shape and no day (review W14). */
function isRealIsoDate(value) {
    const parts = ISO_DATE_PATTERN.exec(value);
    if (!parts)
        return false;
    const [year, month, day] = [Number(parts[1]), Number(parts[2]), Number(parts[3])];
    const date = new Date(Date.UTC(year, month - 1, day));
    return date.getUTCFullYear() === year && date.getUTCMonth() === month - 1 && date.getUTCDate() === day;
}
/** A calendar day. An impossible one is refused here, so it is a 400 with a sentence, never a failed query (W14). */
exports.isoDateSchema = zod_1.z
    .string()
    .trim()
    .regex(ISO_DATE_PATTERN, 'Enter a date in the form 2026-03-26.')
    .refine((value) => !ISO_DATE_PATTERN.test(value) || isRealIsoDate(value), "That date doesn't exist. Check the day and month.");
const workingDaysSchema = zod_1.z
    .number()
    .int()
    .min(1, 'Choose at least one working day.')
    .max(exports.WORKING_DAYS_MAX);
const calendarBodySchema = zod_1.z.object({
    name: zod_1.z.string().trim().min(1, 'Enter a name.').max(120),
    timezone: exports.timezoneSchema,
    workingDays: workingDaysSchema,
    startTime: exports.timeOfDaySchema,
    endTime: exports.timeOfDaySchema,
    /** Null on both means the system default. A department requires its venture. */
    ventureId: schemas_1.uuidSchema.nullable().optional(),
    departmentId: schemas_1.uuidSchema.nullable().optional(),
});
/**
 * An overnight working day is out of scope for S6: a job's "6pm-3am BD time" is a
 * display field on the posting (S11) and never enters a calculation. Rejecting it
 * here rather than at the database keeps the message a sentence instead of a
 * constraint name.
 */
function closesAfterItOpens(value) {
    return value.endTime > value.startTime;
}
const CLOSING_TIME_MESSAGE = {
    message: 'The closing time must be later in the day than the opening time.',
    path: ['endTime'],
};
exports.createCalendarSchema = calendarBodySchema.refine(closesAfterItOpens, CLOSING_TIME_MESSAGE);
exports.updateCalendarSchema = calendarBodySchema
    .extend({ version: zod_1.z.number().int().nonnegative() })
    .refine(closesAfterItOpens, CLOSING_TIME_MESSAGE);
exports.addHolidaySchema = zod_1.z.object({
    holidayDate: exports.isoDateSchema,
    name: zod_1.z.string().trim().min(1, 'Enter a name for the holiday.').max(120),
});
exports.holidayYearQuerySchema = zod_1.z.object({
    year: zod_1.z.coerce.number().int().min(2000).max(2100).optional(),
});
exports.assignUserCalendarSchema = zod_1.z.object({
    /** Null clears the override and returns the person to their department's calendar. */
    workCalendarId: schemas_1.uuidSchema.nullable(),
    version: zod_1.z.number().int().nonnegative(),
});
/**
 * Why a given calendar applies. Shown on screen, because "which calendar am I on?"
 * is a question the answer to which is otherwise invisible until a number looks
 * wrong.
 */
exports.CALENDAR_SOURCES = ['user', 'department', 'venture', 'system_default'];
exports.CALENDAR_SOURCE_LABELS = {
    user: 'Personal override',
    department: 'Department calendar',
    venture: 'Venture calendar',
    system_default: 'System default',
};
//# sourceMappingURL=calendars.js.map